import { FormlyFieldConfig } from './core';
/**
 * @param {?} formId
 * @param {?} options
 * @param {?} index
 * @return {?}
 */
export function getFieldId(formId: string, options: FormlyFieldConfig, index: string|number) {
  if (options.id) return options.id;
  let /** @type {?} */ type = options.type;
  if (!type && options.template) type = 'template';
  return [formId, type, options.key, index].join('_');
}
/**
 * @param {?} field
 * @return {?}
 */
export function getKeyPath(field: {key?: string|string[], fieldGroup?: any, fieldArray?: any}): (string|number)[] {
  /* We store the keyPath in the field for performance reasons. This function will be called frequently. */
  if (field['_formlyKeyPath'] !== undefined) {
    return field['_formlyKeyPath'];
  }
  let /** @type {?} */ keyPath: (string|number)[] = [];
  if (field.key) {
    /* Also allow for an array key, hence the type check  */
    let /** @type {?} */ pathElements = typeof field.key === 'string' ? field.key.split('.') : field.key;
    for (let /** @type {?} */ pathElement of pathElements) {
      if (typeof pathElement === 'string') {
        /* replace paths of the form names[2] by names.2, cfr. angular formly */
        pathElement = pathElement.replace(/\[(\w+)\]/g, '.$1');
        keyPath = keyPath.concat(pathElement.split('.'));
      } else {
        keyPath.push(pathElement);
      }
    }
    for (let /** @type {?} */ i = 0; i < keyPath.length; i++) {
      let /** @type {?} */ pathElement = keyPath[i];
      if (typeof pathElement === 'string' && stringIsInteger(pathElement))  {
        keyPath[i] = parseInt(pathElement);
      }
    }
  }
  field['_formlyKeyPath'] = keyPath;
  return keyPath;
}
/**
 * @param {?} str
 * @return {?}
 */
function stringIsInteger(str: string) {
  return !isNullOrUndefined(str) && /^\d+$/.test(str);
}
/**
 * @param {?} model
 * @param {?} field
 * @param {?} constructEmptyObjects
 * @return {?}
 */
export function getFieldModel(model: any, field: FormlyFieldConfig, constructEmptyObjects: boolean): any {
  let /** @type {?} */ keyPath: (string|number)[] = getKeyPath(field);
  let /** @type {?} */ value: any = model;
  for (let /** @type {?} */ i = 0; i < keyPath.length; i++) {
    let /** @type {?} */ path = keyPath[i];
    let /** @type {?} */ pathValue = value[path];
    if (isNullOrUndefined(pathValue) && constructEmptyObjects) {
      if (i < keyPath.length - 1) {
        /* TODO? : It would be much nicer if we could construct object instances of the correct class, for instance by using factories. */
        value[path] = typeof keyPath[i + 1] === 'number' ? [] : {};
      } else if (field.fieldGroup) {
        value[path] = {};
      } else if (field.fieldArray) {
        value[path] = [];
      }
    }
    value = value[path];
    if (!value) {
      break;
    }
  }
  return value;
}
/**
 * @param {?} model
 * @param {?} path
 * @param {?} value
 * @return {?}
 */
export function assignModelValue(model, path, value) {
  if (typeof path === 'string') {
    path = getKeyPath({key: path});
  }

  if (path.length > 1) {
    const /** @type {?} */ e = path.shift();
    if (!model[e] || !isObject(model[e])) {
      model[e] = isNaN(path[0]) ? {} : [];
    }
    assignModelValue(model[e], path, value);
  } else {
    model[path[0]] = value;
  }
}
/**
 * @param {?} model
 * @param {?} path
 * @return {?}
 */
export function getValueForKey(model, path) {
  if (typeof path === 'string') {
    path = getKeyPath({key: path});
  }
  if (path.length > 1) {
    const /** @type {?} */ e = path.shift();
    if (!model[e]) {
      model[e] = isNaN(path[0]) ? {} : [];
    }
    return getValueForKey(model[e], path);
  } else {
    return model[path[0]];
  }
}
/**
 * @param {?} controlKey
 * @param {?} actualKey
 * @return {?}
 */
export function getKey(controlKey: string, actualKey: string) {
  return actualKey ? actualKey + '.' + controlKey : controlKey;
}
/**
 * @param {?} dest
 * @param {?=} source
 * @return {?}
 */
export function reverseDeepMerge(dest, source = undefined) {
  let /** @type {?} */ args = Array.prototype.slice.call(arguments);
  if (!args[1]) {
    return dest;
  }
  args.forEach((src, index) => {
    if (!index) {
      return;
    }
    for (let /** @type {?} */ srcArg in src) {
      if (isNullOrUndefined(dest[srcArg]) || isBlankString(dest[srcArg])) {
        if (isFunction(src[srcArg])) {
          dest[srcArg] = src[srcArg];
        } else {
          dest[srcArg] = clone(src[srcArg]);
        }
      } else if (objAndSameType(dest[srcArg], src[srcArg])) {
        reverseDeepMerge(dest[srcArg], src[srcArg]);
      }
    }
  });
  return dest;
}
/**
 * @param {?} value
 * @return {?}
 */
export function isNullOrUndefined(value: any) {
  return value === undefined || value === null;
}
/**
 * @param {?} value
 * @return {?}
 */
export function isUndefined(value: any) {
  return value === undefined;
}
/**
 * @param {?} value
 * @return {?}
 */
export function isBlankString(value: any) {
  return value === '';
}
/**
 * @param {?} value
 * @return {?}
 */
export function isFunction(value: any) {
  return typeof(value) === 'function';
}
/**
 * @param {?} obj1
 * @param {?} obj2
 * @return {?}
 */
export function objAndSameType(obj1, obj2) {
  return isObject(obj1) && isObject(obj2) &&
    Object.getPrototypeOf(obj1) === Object.getPrototypeOf(obj2);
}
/**
 * @param {?} x
 * @return {?}
 */
export function isObject(x: any) {
  return x != null && typeof x === 'object';
}
/**
 * @param {?} value
 * @return {?}
 */
export function clone(value: any) {
  if (!isObject(value)) {
    return value;
  }

  if (Object.prototype.toString.call(value) === '[object Date]') {
    return new Date(value.getTime());
  }

  return Array.isArray(value) ? value.slice(0) : Object.assign({}, value);
}
/**
 * @param {?} expression
 * @param {?} argNames
 * @return {?}
 */
export function evalStringExpression(expression: string, argNames: string[]) {
  try {
    return Function.bind.apply(Function, [void 0].concat(argNames.concat(`return ${expression};`)))();
  } catch ( /** @type {?} */error) {
    console.error(error);
  }
}
/**
 * @param {?} expression
 * @param {?} argNames
 * @return {?}
 */
export function evalExpressionValueSetter(expression: string, argNames: string[]) {
  try {
    return Function.bind
      .apply(Function, [void 0].concat(argNames.concat(`${expression} = expressionValue;`)))();
  } catch ( /** @type {?} */error) {
    console.error(error);
  }
}
/**
 * @param {?} expression
 * @param {?} thisArg
 * @param {?} argVal
 * @return {?}
 */
export function evalExpression(expression: string | Function | boolean, thisArg: any, argVal: any[]): any {
  if (expression instanceof Function) {
    return expression.apply(thisArg, argVal);
  } else {
    return expression ? true : false;
  }
}
